<template>
  <SidebarLayout classname="section-np import-page">
    <div class="section section-import">
      <div class="import-container">
        <ImportHeader :project="project" @reupload="openReuploadModal" />

        <div class="table-import-wrap">
          <TabsSelector>
            <Tab
              v-for="(list, i) in lists"
              :id="list.title"
              :key="list.id"
              :title="list.title"
              :index="i"
              :selected="list.id === activeList"
              @click.native="loadList(list.id, true)"
              @remove="removeList(list.id)"
            >
              <DropdownMenu>
                <DropdownMenuElement @click.native="removeList(list.id)">
                  {{ $t('Delete list') }}
                  <template slot="icon">
                    <Icon type="trash" size="16" />
                  </template>
                </DropdownMenuElement>
              </DropdownMenu>
            </Tab>
          </TabsSelector>
          <div class="table-wrapper import">
            <ImportList
              v-if="list"
              :list="list"
              :errors="listErrors"
              :loading="listLoading"
              @removeRow="removeRow"
              @update="loadList(null, true)"
              @edit-cell-value="editCellValue"
            />
          </div>
          <ImportActions
            :errors="importActionsErrors"
            @import="importProject"
            @delete-project="deleteProject"
          />
        </div>
      </div>
    </div>
  </SidebarLayout>
</template>

<script>
  import { mapActions } from 'vuex';
  import ImportHeader from '@/containers/import/Header';
  import ImportActions from '@/containers/import/Actions';
  import ImportList from '@/containers/import/List';
  import projectErrorsSubscription from '@/gql/import/project.graphql';
  import protoQuery from '@/gql/import/proto-project.graphql';
  import listQuery from '@/gql/import/proto-list.graphql';
  import SidebarLayout from '@/layouts/SidebarLayout';
  import factory from '@/utils/api/factory';
  import parseError from '@/utils/formatting/parse-error';
  import importErrors from '@/utils/fields/import-errors';
  import FileModal from '@/containers/import/FileModal';
  import request from '@/utils/api/request';
  import Tab from '@/components/tabs/Tab';
  import DropdownMenuElement from '@/components/ui/dropdown-menu/DropdownMenuElement';
  import TabsSelector from '@/components/tabs/TabsSelector';
  import Icon from '@/components/ui/Icon';
  import DropdownMenu from '@/components/ui/dropdown-menu/DropdownMenu';

  export default {
    name: 'ImportPage',
    components: {
      DropdownMenu,
      TabsSelector,
      Tab,
      SidebarLayout,
      ImportList,
      ImportActions,
      ImportHeader,
      DropdownMenuElement,
      Icon,
    },
    props: {
      id: [String, Number],
    },
    apollo: {
      $subscribe: {
        projectErrorsInfo() {
          return {
            query: projectErrorsSubscription,
            fetchPolicy: 'no-cache',
            variables: () => ({ id: this.id }),
            result({ data }) {
              if (data) this.project = data?.project?.[0] || {};
              else this.$router.push({ name: 'projects' });
            },
          };
        },
      },

      lists() {
        if (isNaN(this.id)) this.$router.push({ name: 'projects' });
        if (!this.silentLoad) this.listLoading = true;

        return {
          query: protoQuery,
          fetchPolicy: 'no-cache',
          variables: () => ({ id: this.id }),
          update(data) {
            if (data) {
              this.listLoading = false;
              this.silentLoad = true;

              const lists = data.proto_list;
              const active = lists?.[0];

              if (!active) {
                this.list = null;
                this.loading = false;
              }

              return lists;
            }

            this.$router.push({ name: 'projects' });
            return {};
          },
        };
      },
    },
    data() {
      return {
        project: {},
        list: null, // object
        activeList: null, // id
        listLoading: true,
        silentLoad: false,
        loading: false,
      };
    },
    computed: {
      errors() {
        const errors = this.project?.errors?.import_errors || [];
        return importErrors(errors);
      },
      listErrors() {
        return this.errors.find((list) => list.name === this.activeListName)?.rows || null;
      },
      activeListName() {
        return this.list?.title;
      },
      importActionsErrors() {
        return this.project?.errors?.import_errors;
      },
    },
    watch: {
      async lists(value) {
        const id = +this.$route.params.listId || value?.[0].id;
        await this.loadList(id, true);
      },
    },
    methods: {
      ...mapActions({
        createError: 'error/create',
        openModal: 'modal/openModal',
      }),
      editCellValue(params) {
        const rowIndex = this.list.rows.findIndex((row) => row.id === params.rowId);
        // eslint-disable-next-line no-prototype-builtins
        if (this.list.rows[rowIndex].attrs.hasOwnProperty(params.key)) {
          this.list.rows[rowIndex].attrs[params.key] = params.error
            ? ` ${params.oldValue}`
            : ` ${params.value}`;
        } else {
          this.list.rows[rowIndex].attrs.custom_fields[params.key] = params.error
            ? ` ${params.oldValue}`
            : ` ${params.value}`;
        }
      },
      async updateTable() {
        await this.loadList(null, true);
      },
      async deleteProject() {
        try {
          await request.delete(`projects/${this.project.id}`);
          await this.$router.push({ name: 'projects' });
        } catch (e) {
          console.error(e);
        }
      },
      async loadList(listId = null, silent = false) {
        await this.$router.push({
          name: 'import-list',
          params: { listId: listId || this.activeList },
        });
        this.listLoading = true;
        try {
          if (!silent) this.loading = true;
          if (!listId) listId = this.activeList;
          this.activeList = listId;
          const { data } = await this.$apollo.query({
            query: listQuery,
            fetchPolicy: 'no-cache',
            variables: { id: listId },
          });
          this.loading = false;
          if (!data) console.error("Couldn't load the list with id: " + listId); // eslint-disable-line no-console
          this.list = this.setRowsIndexes(data?.lists?.[0]);
        } catch (e) {
          this.loading = false;
          console.error(e); // eslint-disable-line no-console
        }
        this.listLoading = false;
      },

      // Накидываем строкам их порядковые индексы (1, 2, 3, 3.1, 3.2, 4 ...)
      setRowsIndexes(list) {
        let currentIndex = 0;
        let currentParentIndex = 0;
        // Если все первые строки не имеют родителя (group_level: 0), то увеличивать родительский каунтер
        let parentGroupLevelFound = false;
        return {
          ...list,
          rows: list.rows.map((row) => {
            if (row.attrs.group_level) {
              parentGroupLevelFound = true;
              currentIndex = 0;
              currentParentIndex += 1;
              return { ...row, rowIndex: currentParentIndex };
            } else if (!parentGroupLevelFound) {
              currentParentIndex += 1;
              return { ...row, rowIndex: currentParentIndex };
            } else {
              currentIndex += 1;
              return { ...row, rowIndex: +`${currentParentIndex}.${currentIndex}` };
            }
          }),
        };
      },

      async removeRow(id) {
        try {
          await factory('import').deleteInPath('row', id);
          this.list.rows = this.list.rows.filter((row) => row.id !== id);
          this.list = this.setRowsIndexes(this.list);
        } catch (e) {
          console.error(parseError(e) || e); // eslint-disable-line
          this.createError(parseError(e) || e);
        }
      },

      async removeList(id) {
        try {
          await factory('import').deleteInPath('list', id);
          await this.$apollo.queries.lists.refetch();
        } catch (e) {
          console.error(parseError(e) || e); // eslint-disable-line
          this.createError(parseError(e) || e);
        }
      },

      async importProject() {
        let res = null;
        try {
          res = await factory('projects').postInResourcePath(this.id, 'import-proto');
        } catch (e) {
          console.error(e); // eslint-disable-line
          this.createError(parseError(e) || e);
        }
        if (res) {
          await this.$router.push({
            name: 'project',
            params: { id: this.id },
          });
        }
      },

      openReuploadModal() {
        this.openModal({
          component: FileModal,
          context: {
            id: this.id,
            update: () => {
              this.$apollo.queries.lists.refetch();
            },
            attributes: {
              class: 'reupload-file-modal new-project-modal',
            },
          },
        });
      },
    },
  };
</script>
<style lang="scss">
  @import 'src/styles/pages/import-page';

  .import-container {
    flex-direction: column;
    display: flex;
    width: 100%;
    height: 100vh;
    margin-right: auto;
    margin-left: auto;
  }

  .import-container.container-flexcenter {
    display: -webkit-box;
    display: -webkit-flex;
    display: -ms-flexbox;
    display: flex;
    height: 100%;
    -webkit-box-orient: vertical;
    -webkit-box-direction: normal;
    -webkit-flex-direction: column;
    -ms-flex-direction: column;
    flex-direction: column;
    -webkit-box-pack: center;
    -webkit-justify-content: center;
    -ms-flex-pack: center;
    justify-content: center;
    -webkit-box-align: center;
    -webkit-align-items: center;
    -ms-flex-align: center;
    align-items: center;
    font-family: noto-sans, sans-serif;
  }
  .table-wrapper.import {
    flex: 1;
    overflow: scroll;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    margin-top: 0;
  }
</style>
